import type { OnboardingData } from "./types";
import {
  buildOnboardingHistory,
  type OnboardingRouteEntry,
} from "./onboardingFlow";

export type OnboardingFixtureScenario = {
  data: Partial<OnboardingData>;
  routes: OnboardingRouteEntry[];
};

function parseFixtureUrl(path: string) {
  try {
    const url = new URL(path, "https://fixture.louise.local");
    if (url.searchParams.get("test") !== "1") return null;
    return url;
  } catch {
    return null;
  }
}

function getFixtureTarget(pathname: string): OnboardingRouteEntry | null {
  if (pathname.endsWith("/onboarding/goals")) {
    return { name: "WelcomeGoals" };
  }
  if (pathname.endsWith("/onboarding/family-building-path")) {
    return { name: "FamilyBuildingPath" };
  }

  const journeyMatch = pathname.match(
    /\/onboarding\/journey\/(preserve|grow)\/?$/,
  );
  if (journeyMatch?.[1] === "preserve" || journeyMatch?.[1] === "grow") {
    return {
      name: "JourneyStage",
      params: { stage: journeyMatch[1] },
    };
  }

  const treatmentTypeMatch = pathname.match(
    /\/onboarding\/treatment-type\/(getting-ready|in-treatment)\/?$/,
  );
  if (
    treatmentTypeMatch?.[1] === "getting-ready" ||
    treatmentTypeMatch?.[1] === "in-treatment"
  ) {
    return {
      name: "TreatmentType",
      params: { stage: treatmentTypeMatch[1] },
    };
  }

  if (pathname.endsWith("/onboarding/how-are-you-helping")) {
    return { name: "HowAreYouHelping" };
  }
  if (pathname.endsWith("/onboarding/working-with-surrogate")) {
    return { name: "WorkingWithSurrogate" };
  }
  if (pathname.endsWith("/onboarding/sex")) return { name: "Sex" };
  if (pathname.endsWith("/onboarding/birthday")) return { name: "Birthday" };
  if (pathname.endsWith("/onboarding/location")) return { name: "Location" };
  if (pathname.endsWith("/onboarding/account")) {
    return { name: "CreateAccount" };
  }
  return null;
}

/**
 * Every direct fixture represents a reachable onboarding state. Seed only the
 * answers required to reach its target; the shared flow resolver derives the
 * stack from those answers.
 */
function getFixtureData(
  target: OnboardingRouteEntry,
  url: URL,
): Partial<OnboardingData> {
  if (target.name === "JourneyStage") {
    return { primaryIntent: target.params.stage };
  }
  if (target.name === "TreatmentType") {
    return {
      primaryIntent: "grow",
      partnerChoice: "solo",
      gender: "AFAB",
      treatmentIntent: "pregnancy",
      fertilityStage: target.params.stage,
    };
  }
  if (target.name === "FamilyBuildingPath") {
    return { primaryIntent: "grow" };
  }
  if (target.name === "HowAreYouHelping") {
    const journeyRole = url.searchParams.get("dev_helper_role");
    if (journeyRole === "carrier") {
      return {
        primaryIntent: "donate",
        treatmentIntent: "surrogacy",
        journeyRole: "gestational-carrier",
        gender: "AFAB",
      };
    }
    if (journeyRole === "donor") {
      return {
        primaryIntent: "donate",
        treatmentIntent: "egg-donation",
        journeyRole: "donor",
        gender: "AFAB",
      };
    }
    return { primaryIntent: "donate" };
  }
  if (target.name === "WorkingWithSurrogate") {
    return {
      primaryIntent: "grow",
      treatmentIntent: "exploring",
      fertilityStage: "exploring-options",
    };
  }
  if (
    target.name === "Sex" ||
    target.name === "Birthday" ||
    target.name === "Location" ||
    target.name === "CreateAccount"
  ) {
    return {
      primaryIntent: "preserve",
      treatmentIntent: "exploring",
    };
  }
  return {};
}

export function getOnboardingFixtureScenario(
  path: string,
): OnboardingFixtureScenario | null {
  const url = parseFixtureUrl(path);
  if (!url) return null;

  const target = getFixtureTarget(url.pathname.replace(/\/$/, ""));
  if (!target) return null;

  const data = getFixtureData(target, url);
  const routes = buildOnboardingHistory(target, data);
  return routes ? { data, routes } : null;
}

export function getCurrentOnboardingFixtureScenario(): OnboardingFixtureScenario | null {
  if (typeof window === "undefined") return null;
  return getOnboardingFixtureScenario(
    `${window.location.pathname}${window.location.search}`,
  );
}
